import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAgentStore } from '../stores/agent';
import { apiFetch } from '../lib/api';
import { getSiteAnalytics, type SiteAnalytics } from '../lib/analytics-api';
import { Skeleton } from '../components/ui';
import { CostPanel } from './CostPanel';
import styles from './Analytics.module.css';

interface SiteLite {
  id: string;
  name: string;
  slug: string;
}

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;

const DAY_MS = 86_400_000;

function fmtDuration(sec: number): string {
  if (!sec || sec < 1) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${Math.round(sec % 60)}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
const fmtInt = (n: number): string => n.toLocaleString();

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM-DD' → 'Jun 3' (parsed by parts to avoid timezone drift). */
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return m && d ? `${MONTHS[m - 1]} ${d}` : iso;
}
const pad2 = (n: number): string => String(n).padStart(2, '0');

export function Analytics() {
  const role = useAgentStore((s) => s.current?.role);
  const isSuperAdmin = role === 'super-admin';
  const [sites, setSites] = useState<SiteLite[]>([]);
  const [siteId, setSiteId] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SiteAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ sites: SiteLite[] }>('/api/admin/sites')
      .then((r) => {
        setSites(r.sites);
        setSiteId((prev) => prev || r.sites[0]?.id || '');
        if (r.sites.length === 0) setLoading(false);
      })
      .catch(() => {
        setError('Couldn’t load sites.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!siteId) return;
    let ignore = false;
    setLoading(true);
    setError(null);
    const to = new Date();
    const from = new Date(to.getTime() - days * DAY_MS);
    getSiteAnalytics(siteId, from.toISOString(), to.toISOString())
      .then((d) => {
        if (!ignore) setData(d);
      })
      .catch(() => {
        if (!ignore) setError('Couldn’t load analytics for this site.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [siteId, days]);

  const toggle = (key: string) => setOpen((o) => (o === key ? null : key));

  // Chats chart granularity: per calendar day, or per hour-of-day (busiest
  // hours). Hour buckets are filled 0–23 so the axis is always complete.
  const [chartMode, setChartMode] = useState<'day' | 'hour'>('day');
  const chartRows = useMemo<
    Array<{ key: string; label: string; full: string; agent: number; ai: number; none: number }>
  >(() => {
    if (!data) return [];
    if (chartMode === 'hour') {
      const m = new Map(data.busiestHours.map((h) => [h.hour, h]));
      return Array.from({ length: 24 }, (_, h) => {
        const r = m.get(h);
        return {
          key: `h${h}`,
          label: `${h}`,
          full: `${pad2(h)}:00`,
          agent: r?.agent ?? 0,
          ai: r?.ai ?? 0,
          none: r?.none ?? 0,
        };
      });
    }
    return data.chats.daily.map((d) => ({
      key: d.date,
      label: shortDate(d.date),
      full: shortDate(d.date),
      agent: d.agent,
      ai: d.ai,
      none: d.none,
    }));
  }, [data, chartMode]);
  const labelStride = Math.max(1, Math.ceil(chartRows.length / 8));

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <div className={styles.kickerBadge}>
            <span className={styles.kickerDot} />
            <span className={styles.kickerText}>REAL-TIME TELEMETRY & INSIGHTS</span>
          </div>
          <h1 className={styles.title}>Site insights</h1>
        </div>
        <div className={styles.controls}>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Site</span>
            <select
              className={styles.select}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.rangeTabs} role="tablist" aria-label="Date range">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                role="tab"
                aria-selected={days === r.days}
                className={[styles.rangeTab, days === r.days ? styles.rangeTabActive : ''].join(' ')}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {!error && sites.length === 0 ? (
        <p className={styles.empty}>No sites are assigned to you.</p>
      ) : null}
      {loading ? (
        <div className="space-y-4 my-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-4 rounded-xl border border-border bg-card space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-2 w-28" />
              </div>
            ))}
          </div>
          <div className="p-6 rounded-xl border border-border bg-card space-y-4">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      ) : null}

      {!loading && data ? (
        <>
          {/* Headline stats — each expands a breakdown; some link into the app. */}
          <div className={styles.statGrid}>
            <Stat
              k="chats"
              label="Chats"
              numValue={data.chats.total}
              open={open}
              onToggle={toggle}
              link={{ to: '/dashboard', label: 'Open Monitor' }}
            >
              <Bars
                rows={[
                  { label: 'Chat', value: data.chats.byMedium.chat },
                  { label: 'Video', value: data.chats.byMedium.video },
                  { label: 'Agent-handled', value: data.chats.byOwner.agent },
                  { label: 'AI-handled', value: data.chats.byOwner.ai },
                ]}
              />
            </Stat>

            <Stat
              k="visitors"
              label="Visitors seen"
              numValue={data.visitors.seen}
              open={open}
              onToggle={toggle}
              link={{ to: '/dashboard', label: 'Open Monitor' }}
            >
              <Bars
                rows={[
                  { label: 'New', value: data.visitors.new },
                  { label: 'Returning', value: data.visitors.returning },
                  { label: 'Identified', value: data.visitors.identified },
                ]}
              />
            </Stat>

            <Stat
              k="conversion"
              label="Identified"
              numValue={data.visitors.conversionPct}
              decimals={data.visitors.conversionPct < 10 ? 1 : 0}
              suffix="%"
              open={open}
              onToggle={toggle}
            >
              <p className={styles.drillText}>
                {fmtInt(data.visitors.identified)} of {fmtInt(data.visitors.seen)} visitors shared
                contact details.
              </p>
            </Stat>

            <Stat
              k="deflection"
              label="AI deflection"
              numValue={data.chats.aiDeflectionPct}
              decimals={data.chats.aiDeflectionPct < 10 ? 1 : 0}
              suffix="%"
              open={open}
              onToggle={toggle}
            >
              <p className={styles.drillText}>
                AI handled {fmtInt(data.chats.byOwner.ai)} of{' '}
                {fmtInt(data.chats.byOwner.ai + data.chats.byOwner.agent)} owned chats end-to-end.
              </p>
            </Stat>

            <Stat
              k="response"
              label="Avg first reply"
              value={fmtDuration(data.resolution.avgFirstResponseSec)}
              open={open}
              onToggle={toggle}
            >
              <p className={styles.drillText}>
                Avg chat duration {fmtDuration(data.resolution.avgDurationSec)} ·{' '}
                {data.resolution.avgMessages} messages per chat.
              </p>
            </Stat>

            <Stat
              k="webhooks"
              label="Webhook fires"
              numValue={data.webhooks.total}
              open={open}
              onToggle={toggle}
              link={isSuperAdmin ? { to: '/admin/logs', label: 'Open delivery logs' } : undefined}
            >
              <Bars
                rows={[
                  { label: 'session.ended', value: data.webhooks.byEvent['session.ended'] ?? 0 },
                  {
                    label: 'visitor.identified',
                    value: data.webhooks.byEvent['visitor.identified'] ?? 0,
                  },
                  { label: 'Succeeded', value: data.webhooks.byStatus.success },
                  { label: 'Failed', value: data.webhooks.byStatus.failed },
                  { label: 'Skipped', value: data.webhooks.byStatus.skipped },
                ]}
              />
            </Stat>
          </div>

          {/* Chats chart — switch day / hour; hover a bar for its data point. */}
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <p className={styles.panelTitle}>
                {chartMode === 'day' ? 'Chats per day' : 'Chats by hour of day'}
              </p>
              <div className={styles.rangeTabs} role="tablist" aria-label="Chart granularity">
                <button
                  type="button"
                  role="tab"
                  aria-selected={chartMode === 'day'}
                  className={[styles.rangeTab, chartMode === 'day' ? styles.rangeTabActive : ''].join(' ')}
                  onClick={() => setChartMode('day')}
                >
                  By day
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={chartMode === 'hour'}
                  className={[styles.rangeTab, chartMode === 'hour' ? styles.rangeTabActive : ''].join(' ')}
                  onClick={() => setChartMode('hour')}
                >
                  By hour
                </button>
              </div>
            </div>
            {chartRows.length === 0 ? (
              <p className={styles.drillText}>No chats in this range.</p>
            ) : (
              <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartRows} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                    <CartesianGrid vertical={false} stroke="var(--inic-rule)" />
                    <XAxis
                      dataKey="label"
                      interval={labelStride - 1}
                      tick={{ fill: 'var(--inic-ink-subtle)', fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: 'var(--inic-rule)' }}
                    />
                    <YAxis
                      allowDecimals={false}
                      width={36}
                      tick={{ fill: 'var(--inic-ink-subtle)', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--inic-paper-recessed)' }}
                      content={<ChartTooltip />}
                    />
                    <Legend
                      iconType="square"
                      iconSize={9}
                      wrapperStyle={{ fontSize: 11, color: 'var(--inic-ink-subtle)' }}
                    />
                    <Bar stackId="s" dataKey="agent" name="Agent" fill="var(--inic-ink)" maxBarSize={48} />
                    <Bar stackId="s" dataKey="ai" name="AI" fill="var(--inic-accent)" maxBarSize={48} />
                    <Bar
                      stackId="s"
                      dataKey="none"
                      name="Unassigned"
                      fill="var(--inic-ink-subtle)"
                      maxBarSize={48}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <CostPanel siteId={siteId} days={days} isSuperAdmin={isSuperAdmin} />

          {/* Agents */}
          <div className={styles.panel}>
            <p className={styles.panelTitle}>Agent activity</p>
            {data.agents.length === 0 ? (
              <p className={styles.drillText}>No agent activity in this range.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th className={styles.numCol}>Chats handled</th>
                    <th className={styles.numCol}>Avg duration</th>
                    <th className={styles.numCol}>Active time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.map((a) => (
                    <tr key={a.agentId}>
                      <td>{a.name || a.agentId.slice(0, 8)}</td>
                      <td className={styles.numCol}>{fmtInt(a.chatsHandled)}</td>
                      <td className={styles.numCol}>{fmtDuration(a.avgDurationSec)}</td>
                      <td className={styles.numCol}>{fmtDuration(a.activeSec)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Resolution + top pages + geo in a two-column band */}
          <div className={styles.band}>
            <div className={styles.panel}>
              <p className={styles.panelTitle}>How chats ended</p>
              {data.resolution.endReasons.length === 0 ? (
                <p className={styles.drillText}>No chats in this range.</p>
              ) : (
                <Bars
                  rows={data.resolution.endReasons.map((r) => ({
                    label: r.reason === 'open' ? 'still open' : r.reason,
                    value: r.count,
                  }))}
                />
              )}
            </div>
            <div className={styles.panel}>
              <p className={styles.panelTitle}>Top pages</p>
              {data.topPages.length === 0 ? (
                <p className={styles.drillText}>No page visits in this range.</p>
              ) : (
                <ul className={styles.list}>
                  {data.topPages.map((p) => (
                    <li key={p.url} className={styles.listRow}>
                      <span className={styles.listLabel} title={p.url}>
                        {p.url}
                      </span>
                      <span className={styles.listValue}>{fmtInt(p.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={styles.panel}>
              <p className={styles.panelTitle}>Visitors by country</p>
              {data.geo.length === 0 ? (
                <p className={styles.drillText}>No visitor data in this range.</p>
              ) : (
                <ul className={styles.list}>
                  {data.geo.map((g) => (
                    <li key={g.country} className={styles.listRow}>
                      <span className={styles.listLabel}>{g.country}</span>
                      <span className={styles.listValue}>{fmtInt(g.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}

// ── A headline stat card. Clicking toggles a breakdown drawer below; an
//    optional link jumps to the matching detailed view in the app. ──
function Stat({
  k,
  label,
  value,
  numValue,
  decimals,
  suffix,
  open,
  onToggle,
  link,
  children,
}: {
  k: string;
  label: string;
  value?: string;
  numValue?: number;
  decimals?: number;
  suffix?: string;
  open: string | null;
  onToggle: (k: string) => void;
  link?: { to: string; label: string };
  children: React.ReactNode;
}) {
  const isOpen = open === k;
  return (
    <div className={[styles.stat, isOpen ? styles.statOpen : ''].join(' ')}>
      <button
        type="button"
        className={styles.statBtn}
        onClick={() => onToggle(k)}
        aria-expanded={isOpen}
      >
        <span className={styles.statValue}>
          {numValue !== undefined ? (
            `${numValue.toLocaleString(undefined, {
              minimumFractionDigits: decimals ?? 0,
              maximumFractionDigits: decimals ?? 0,
            })}${suffix ?? ''}`
          ) : (
            value ?? '—'
          )}
        </span>
        <span className={styles.statLabel}>{label}</span>
      </button>
      {isOpen ? (
        <div className={styles.drill}>
          {children}
          {link ? (
            <Link className={styles.drillLink} to={link.to}>
              {link.label} →
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── Styled hover tooltip for the chats chart — header (date/hour + total) and
// one line per status segment. Recharts passes `active` + `payload` at runtime;
// typed locally since v3's exported TooltipProps doesn't surface them. ──
interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: { full?: string; agent: number; ai: number; none: number };
  }>;
}
function ChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const total = row.agent + row.ai + row.none;
  return (
    <div className={styles.rtip}>
      <div className={styles.rtipHead}>
        {row.full} · {total} {total === 1 ? 'chat' : 'chats'}
      </div>
      {payload
        .filter((p) => (p.value ?? 0) > 0)
        .map((p) => (
          <div key={p.name} className={styles.rtipRow}>
            <span className={styles.rtipDot} style={{ background: p.color }} aria-hidden />
            {p.name}: {p.value}
          </div>
        ))}
    </div>
  );
}

// ── Horizontal labelled bars, scaled to the largest value in the set. ──
function Bars({ rows }: { rows: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className={styles.bars}>
      {rows.map((r) => (
        <li key={r.label} className={styles.barRow}>
          <span className={styles.barLabel}>{r.label}</span>
          <span className={styles.barTrack}>
            <span className={styles.barFill} style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className={styles.barValue}>{r.value.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}
